import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import SignatureCanvas from 'react-signature-canvas';
import {
  Users, Eye, Search, ChevronRight, Loader2, CheckCircle,
  Globe, Instagram, Music, ExternalLink, Upload, FileText,
  Send, X, Clock, AlertCircle, Image, MapPin, Tag,
  ArrowLeft, ArrowRight, PenTool, Download, UserCheck,
  Bold, Italic, UnderlineIcon, Heading1, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  Undo, Redo, RotateCcw, Film, FolderOpen, Mic, Target
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './ArtistProfileReview.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (url) => {
  if (!url) return null;
  return getImageUrl(url) || '#';
};

const ArtistProfileReview = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistDetail, setArtistDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [artistMedia, setArtistMedia] = useState(null);

  // Document Generation Wizard
  const [showDocWizard, setShowDocWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [requiresCountersign, setRequiresCountersign] = useState(false);
  const [populatedHtml, setPopulatedHtml] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Counter-sign modal
  const [countersignDoc, setCountersignDoc] = useState(null);
  const [staffName, setStaffName] = useState('');
  const [countersigning, setCountersigning] = useState(false);
  const staffSigRef = useRef(null);
  const staffPadWrapperRef = useRef(null);
  const [staffPadWidth, setStaffPadWidth] = useState(380);

  // PDF viewer
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null);

  const getToken = () => localStorage.getItem('adminToken');
  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => { fetchArtists(); }, [filterStatus]);

  // ResizeObserver for staff counter-sign signature pad
  useEffect(() => {
    if (!countersignDoc || !staffPadWrapperRef.current) return;
    const el = staffPadWrapperRef.current;
    const measure = () => {
      const w = el.offsetWidth;
      setStaffPadWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [countersignDoc]);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const url = filterStatus
        ? `${API_URL}/api/staff/artists?status=${filterStatus}`
        : `${API_URL}/api/staff/artists`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) setArtists(await res.json());
    } catch {
      toast.error('Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistDetail = async (artistId) => {
    setDetailLoading(true);
    try {
      const [detailRes, docsRes, mediaRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/artists/${artistId}`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/staff/artists/${artistId}/documents`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/staff/artists/${artistId}/media`, { headers: getHeaders() })
      ]);
      if (detailRes.ok) setArtistDetail(await detailRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (mediaRes.ok) setArtistMedia(await mediaRes.json());
    } catch {
      toast.error('Failed to load artist details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectArtist = (artist) => {
    setSelectedArtist(artist);
    fetchArtistDetail(artist.id);
  };

  const handleToggleActive = async () => {
    if (!selectedArtist) return;
    const isActive = artistDetail?.active;
    if (!window.confirm(`${isActive ? 'Deactivate' : 'Activate'} ${selectedArtist.artist_name}'s public profile?`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/artists/${selectedArtist.id}/toggle-active`, {
        method: 'PUT', headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchArtists();
        fetchArtistDetail(selectedArtist.id);
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Document Generation Wizard ──

  const openDocWizard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/document-templates`, { headers: getHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch { /* silent */ }
    setWizardStep(1);
    setSelectedTemplate(null);
    setCustomFieldValues({});
    setRequiresCountersign(false);
    setPopulatedHtml('');
    setPreviewUrl(null);
    setShowDocWizard(true);
  };

  const closeDocWizard = () => {
    setShowDocWizard(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    // Pre-initialize custom field values
    const vals = {};
    (tmpl.custom_fields || []).forEach(f => { vals[f.name] = ''; });
    setCustomFieldValues(vals);
    // Auto-detect counter-signature need from template content
    setRequiresCountersign(tmpl.content_html?.includes('{{staff_signature_block}}') || false);
    setWizardStep(2);
  };

  const handlePopulateTemplate = async () => {
    // Validate required custom fields
    const missing = (selectedTemplate.custom_fields || []).filter(f => f.required && !customFieldValues[f.name]?.trim());
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/artists/${selectedArtist.id}/populate-template`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          custom_fields: customFieldValues,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPopulatedHtml(data.populated_html);
        setWizardStep(3);
      } else {
        toast.error('Failed to populate template');
      }
    } catch {
      toast.error('Failed to populate template');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewPdf = async (htmlOverride) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/artists/${selectedArtist.id}/preview-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          custom_fields: customFieldValues,
          content_html_override: htmlOverride || populatedHtml,
        }),
      });
      if (res.ok) {
        const blob = await res.blob();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        setWizardStep(4);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendDocument = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/artists/${selectedArtist.id}/generate-document`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          custom_fields: customFieldValues,
          content_html_override: populatedHtml,
          requires_countersign: requiresCountersign,
        }),
      });
      if (res.ok) {
        toast.success(`Document sent to ${selectedArtist.artist_name}'s Legal Vault`);
        closeDocWizard();
        fetchArtistDetail(selectedArtist.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch {
      toast.error('Failed to send document');
    } finally {
      setGenerating(false);
    }
  };

  // ── Staff Counter-sign ──

  const handleCountersign = async () => {
    if (!staffName.trim()) { toast.error('Enter your name'); return; }
    if (!staffSigRef.current || staffSigRef.current.isEmpty()) {
      toast.error('Please draw your signature');
      return;
    }
    setCountersigning(true);
    try {
      const sigData = staffSigRef.current.toDataURL('image/png');
      const signatureImage = sigData.replace('data:image/png;base64,', '');
      const res = await fetch(`${API_URL}/api/staff/documents/${countersignDoc.id}/countersign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ staff_name: staffName.trim(), signature_image: signatureImage }),
      });
      if (res.ok) {
        toast.success('Document counter-signed and fully executed');
        setCountersignDoc(null);
        setStaffName('');
        fetchArtistDetail(selectedArtist.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to counter-sign');
      }
    } catch {
      toast.error('Failed to counter-sign');
    } finally {
      setCountersigning(false);
    }
  };

  // ── Distribution Export ──
  const handleExportDistribution = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/artists/${selectedArtist.id}/export-distribution`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'distribution.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('Distribution CSV downloaded');
      } else {
        toast.error('Failed to export');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  // ── Submission File Download ──
  const handleDownloadSubmission = async (submissionId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/artists/${selectedArtist.id}/submissions/${submissionId}/download`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.download_url) {
          window.open(data.download_url, '_blank');
        } else if (data.file_links) {
          data.file_links.forEach(link => window.open(link, '_blank'));
        }
      } else {
        toast.error('Download failed');
      }
    } catch {
      toast.error('Download failed');
    }
  };

  // ── Staff PDF Viewer ──
  const handleStaffViewPdf = async (docId, isSigned = false) => {
    const endpoint = isSigned
      ? `${API_URL}/api/staff/documents/${docId}/signed-pdf`
      : `${API_URL}/api/staff/documents/${docId}/pdf`;
    try {
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        setViewingPdfUrl(URL.createObjectURL(blob));
      } else {
        toast.error('Failed to load PDF');
      }
    } catch {
      toast.error('Failed to load PDF');
    }
  };

  const filteredArtists = artists.filter(a =>
    a.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  }) : 'N/A';

  const profile = artistDetail?.profile;
  const account = artistDetail?.account;

  const statusLabel = (status) => {
    const map = {
      sent: 'Sent', viewed: 'Viewed', signed: 'Signed',
      artist_signed: 'Artist Signed', completed: 'Completed'
    };
    return map[status] || status;
  };

  // ── PDF Viewer Overlay ──
  if (viewingPdfUrl) {
    return (
      <div className="artist-profile-review" data-testid="artist-profile-review">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button className="action-btn link" onClick={() => { URL.revokeObjectURL(viewingPdfUrl); setViewingPdfUrl(null); }} data-testid="close-staff-pdf">
            <ArrowLeft size={16} /> Back
          </button>
          <h2 style={{ fontSize: '1.2rem' }}>Document Preview</h2>
        </div>
        <iframe src={viewingPdfUrl} style={{ width: '100%', height: 'calc(100vh - 160px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }} title="PDF Preview" data-testid="staff-pdf-viewer" />
      </div>
    );
  }

  return (
    <div className="artist-profile-review" data-testid="artist-profile-review">
      <div className="review-header">
        <div className="header-left">
          <p>Review and publish artist portal profiles to the public site</p>
        </div>
      </div>

      <div className="review-content">
        {/* Left: Artist List */}
        <div className="artist-list-panel">
          <div className="list-filters">
            <div className="search-box">
              <Search size={16} />
              <input type="text" placeholder="Search artists..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-artists" />
            </div>
            <div className="filter-pills">
              {['', 'approved', 'pending', 'suspended'].map(s => (
                <button key={s} className={filterStatus === s ? 'active' : ''} onClick={() => setFilterStatus(s)}>
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>

          <div className="artist-list" data-testid="artist-list">
            {loading ? (
              <div className="center-state"><Loader2 className="spinner" size={24} /></div>
            ) : filteredArtists.length === 0 ? (
              <div className="center-state"><p>No artists found</p></div>
            ) : (
              filteredArtists.map(a => (
                <div key={a.id} className={`artist-list-item ${selectedArtist?.id === a.id ? 'selected' : ''}`} onClick={() => handleSelectArtist(a)} data-testid={`artist-item-${a.id}`}>
                  <div className="artist-item-info">
                    <span className="artist-item-name">{a.artist_name}</span>
                    <span className="artist-item-email">{a.email}</span>
                  </div>
                  <div className="artist-item-meta">
                    <span className={`status-pill ${a.status}`}>{a.status}</span>
                    {a.public_artist_id && <Globe size={14} className="linked-icon" title="Linked to public profile" />}
                  </div>
                  <ChevronRight size={16} className="chevron" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div className={`artist-detail-panel ${selectedArtist ? 'open' : ''}`}>
          {!selectedArtist ? (
            <div className="center-state"><p>Select an artist to review</p></div>
          ) : detailLoading ? (
            <div className="center-state"><Loader2 className="spinner" size={24} /></div>
          ) : artistDetail ? (
            <div className="detail-scroll" data-testid="artist-detail">
              {/* Header */}
              <div className="detail-top">
                <div className="detail-avatar">
                  {profile?.profile_image ? (
                    <img src={getFileUrl(profile.profile_image)} alt={account?.artist_name} />
                  ) : (
                    <div className="avatar-placeholder">{account?.artist_name?.charAt(0)}</div>
                  )}
                </div>
                <div className="detail-name-block">
                  <h2>{account?.artist_name}</h2>
                  <span className="detail-email">{account?.email}</span>
                  <span className={`status-pill ${account?.status}`}>{account?.status}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="detail-actions">
                <button className={`action-btn ${artistDetail?.active ? 'deactivate' : 'publish'}`} onClick={handleToggleActive} disabled={actionLoading} data-testid="toggle-active-btn">
                  {actionLoading ? <Loader2 size={16} className="spinner" /> : artistDetail?.active ? <Eye size={16} /> : <CheckCircle size={16} />}
                  {artistDetail?.active ? 'Deactivate' : 'Activate Profile'}
                </button>
                <button className="action-btn doc" onClick={openDocWizard} data-testid="send-doc-btn">
                  <FileText size={16} /> Generate Document
                </button>
                <button className="action-btn export" onClick={handleExportDistribution} data-testid="export-distribution-btn">
                  <Download size={16} /> Export
                </button>
                {artistDetail?.public_slug && (
                  <a href={`/links/${artistDetail.public_slug}`} target="_blank" rel="noopener noreferrer" className="action-btn link">
                    <ExternalLink size={16} /> View Link Page
                  </a>
                )}
                {artistDetail?.public_slug && (
                  <button className="action-btn link" onClick={() => window.open(`/presskit/${artistDetail.public_slug}`, '_blank')} data-testid="view-presskit-btn">
                    <Eye size={16} /> View Press Kit
                  </button>
                )}
                {artistDetail?.public_slug && (
                  <button className="action-btn export" onClick={() => window.open(`${API_URL}/api/presskit/${artistDetail.public_slug}/pdf`, '_blank')} data-testid="presskit-pdf-btn">
                    <Download size={16} /> Press Kit PDF
                  </button>
                )}
              </div>

              {/* Profile Info */}
              <div className="detail-section">
                <h3><Tag size={16} /> Profile Info</h3>
                <div className="info-grid-2col">
                  <div className="info-field"><label>Artist Type</label><span>{profile?.artist_type || '—'}</span></div>
                  <div className="info-field"><label>Genre</label><span>{profile?.main_genre || '—'}</span></div>
                  <div className="info-field"><label>Location</label><span>{[profile?.city, profile?.country].filter(Boolean).join(', ') || '—'}</span></div>
                  <div className="info-field"><label>Public Profile</label><span>{account?.public_artist_id ? 'Linked' : 'Not linked'}</span></div>
                </div>
              </div>

              {/* Bio */}
              {profile?.bio && (
                <div className="detail-section">
                  <h3><FileText size={16} /> Bio</h3>
                  <p className="bio-text">{profile.bio}</p>
                  {profile?.short_bio && <p className="bio-text" style={{marginTop:8}}><strong style={{color:'var(--neon-cyan)',fontSize:'0.68rem',textTransform:'uppercase',letterSpacing:'1px'}}>Short Bio:</strong> {profile.short_bio}</p>}
                  {profile?.medium_bio && <p className="bio-text" style={{marginTop:8}}><strong style={{color:'var(--neon-cyan)',fontSize:'0.68rem',textTransform:'uppercase',letterSpacing:'1px'}}>Medium Bio:</strong> {profile.medium_bio}</p>}
                </div>
              )}

              {/* Identity & Branding */}
              {(profile?.pronunciation || profile?.comparable_artists || profile?.brand_adjectives?.length > 0 || profile?.visual_references) && (
                <div className="detail-section">
                  <h3><Mic size={16} /> Identity & Branding</h3>
                  <div className="detail-grid">
                    {profile?.pronunciation && <div className="detail-item"><label>Pronunciation</label><span>{profile.pronunciation}</span></div>}
                    {profile?.comparable_artists && <div className="detail-item"><label>RIYL / Comparable</label><span>{profile.comparable_artists}</span></div>}
                    {profile?.brand_adjectives?.length > 0 && <div className="detail-item"><label>Brand Adjectives</label><span>{profile.brand_adjectives.join(', ')}</span></div>}
                  </div>
                  {profile?.visual_references && <div style={{marginTop:8}}><label style={{fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--silver)',display:'block',marginBottom:4}}>Visual References</label><p className="bio-text">{profile.visual_references}</p></div>}
                </div>
              )}

              {/* DJ & Promo */}
              {(profile?.beatport_genre || profile?.dj_pitch || profile?.priority_djs || profile?.club_markets) && (
                <div className="detail-section">
                  <h3><Target size={16} /> DJ & Promo</h3>
                  <div className="detail-grid">
                    {profile?.beatport_genre && <div className="detail-item"><label>Beatport Genre</label><span>{profile.beatport_genre}</span></div>}
                    {profile?.secondary_vibe && <div className="detail-item"><label>Secondary Vibe</label><span>{profile.secondary_vibe}</span></div>}
                    {profile?.club_markets && <div className="detail-item"><label>Club Markets</label><span>{profile.club_markets}</span></div>}
                  </div>
                  {profile?.dj_pitch && <div style={{marginTop:8}}><label style={{fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--silver)',display:'block',marginBottom:4}}>DJ Pitch</label><p className="bio-text" style={{color:'var(--neon-cyan)'}}>{profile.dj_pitch}</p></div>}
                  {profile?.priority_djs && <div style={{marginTop:8}}><label style={{fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--silver)',display:'block',marginBottom:4}}>Priority DJs</label><p className="bio-text">{profile.priority_djs}</p></div>}
                  {profile?.existing_dj_support && <div style={{marginTop:8}}><label style={{fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--silver)',display:'block',marginBottom:4}}>Existing DJ Support</label><p className="bio-text">{profile.existing_dj_support}</p></div>}
                  {profile?.target_radio_shows && <div style={{marginTop:8}}><label style={{fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:'var(--silver)',display:'block',marginBottom:4}}>Target Radio Shows</label><p className="bio-text">{profile.target_radio_shows}</p></div>}
                </div>
              )}

              {/* Team Contacts */}
              {profile?.team_contacts?.length > 0 && (
                <div className="detail-section">
                  <h3><Users size={16} /> Team Contacts</h3>
                  {profile.team_contacts.map((tc, i) => (
                    <div key={i} style={{display:'flex',gap:16,padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                      <span style={{fontWeight:700,color:'var(--pure-white)',minWidth:100}}>{tc.name}</span>
                      <span style={{color:'var(--neon-cyan)',fontSize:'0.78rem'}}>{tc.role}</span>
                      <span style={{color:'var(--silver)',fontSize:'0.78rem'}}>{tc.email}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Images */}
              <div className="detail-section">
                <h3><Image size={16} /> Media</h3>
                <div className="media-grid">
                  {profile?.profile_image && <div className="media-item"><label>Profile Photo</label><img src={getFileUrl(profile.profile_image)} alt="Profile" /></div>}
                  {profile?.banner_image && <div className="media-item"><label>Banner</label><img src={getFileUrl(profile.banner_image)} alt="Banner" /></div>}
                  {profile?.press_photos?.length > 0 && (
                    <div className="media-item wide">
                      <label>Press Photos ({profile.press_photos.length})</label>
                      <div className="press-photos-row">
                        {profile.press_photos.map((p, i) => <img key={i} src={getFileUrl(p)} alt={`Press ${i + 1}`} />)}
                      </div>
                    </div>
                  )}
                  {!profile?.profile_image && !profile?.banner_image && (!profile?.press_photos || profile.press_photos.length === 0) && <p className="no-data">No media uploaded yet</p>}
                </div>
              </div>

              {/* Media Vault — uploaded assets */}
              {artistMedia && Object.values(artistMedia).some(cat => cat.files?.length > 0) && (
                <div className="detail-section">
                  <h3><FolderOpen size={16} /> Media Vault</h3>
                  {Object.entries(artistMedia).map(([catKey, catData]) => {
                    if (!catData.files || catData.files.length === 0) return null;
                    return (
                      <div key={catKey} style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--neon-cyan, #00ffff)', display: 'block', marginBottom: 6 }}>{catData.label} ({catData.files.length})</label>
                        {catData.files[0]?.content_type?.startsWith('video/') ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {catData.files.map(f => (
                              <a key={f.id} href={getFileUrl(f.file_url)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, textDecoration: 'none', color: '#fff', fontSize: '0.75rem' }}>
                                <Film size={14} style={{ color: 'var(--neon-cyan)' }} />
                                {f.original_name}
                              </a>
                            ))}
                          </div>
                        ) : catData.files[0]?.content_type?.startsWith('image/') ? (
                          <div className="press-photos-row">
                            {catData.files.map(f => (
                              <a key={f.id} href={getFileUrl(f.file_url)} target="_blank" rel="noopener noreferrer">
                                <img src={getFileUrl(f.file_url)} alt={f.original_name} style={{ maxWidth: 120, maxHeight: 100, objectFit: 'cover', borderRadius: 6 }} />
                              </a>
                            ))}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {catData.files.map(f => (
                              <a key={f.id} href={getFileUrl(f.file_url)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, textDecoration: 'none', color: '#fff', fontSize: '0.75rem' }}>
                                <FileText size={14} style={{ color: 'var(--neon-cyan)' }} />
                                {f.original_name}
                                <Download size={12} style={{ marginLeft: 'auto', color: 'var(--silver)' }} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Social Links */}
              <div className="detail-section">
                <h3><Globe size={16} /> Social & Streaming</h3>
                {renderSocialLinks(profile?.social_links)}
                {renderStreamingIds(profile?.streaming_ids)}
              </div>

              {/* Documents — enhanced with preview + counter-sign */}
              <div className="detail-section">
                <h3><FileText size={16} /> Documents ({documents.length})</h3>
                {documents.length === 0 ? (
                  <p className="no-data">No documents shared yet</p>
                ) : (
                  <div className="documents-list">
                    {documents.map(doc => (
                      <div key={doc.id} className="doc-item" data-testid={`doc-${doc.id}`}>
                        <div className="doc-info">
                          <span className="doc-title">{doc.title}</span>
                          <span className="doc-meta">
                            <span className={`doc-type ${doc.document_type}`}>{doc.document_type}</span>
                            Sent {formatDate(doc.sent_at)}
                          </span>
                        </div>
                        <div className="doc-item-actions">
                          <button className="doc-action-btn" onClick={() => handleStaffViewPdf(doc.id)} title="View original PDF" data-testid={`view-doc-${doc.id}`}>
                            <Eye size={13} /> PDF
                          </button>
                          {(doc.status === 'signed' || doc.status === 'artist_signed' || doc.status === 'completed') && (
                            <button className="doc-action-btn" onClick={() => handleStaffViewPdf(doc.id, true)} title="View signed PDF" data-testid={`view-signed-${doc.id}`}>
                              <Download size={13} /> Signed
                            </button>
                          )}
                          {doc.status === 'artist_signed' && (
                            <button className="doc-action-btn countersign" onClick={() => setCountersignDoc(doc)} data-testid={`countersign-${doc.id}`}>
                              <PenTool size={13} /> Counter-Sign
                            </button>
                          )}
                          <span className={`doc-status ${doc.status}`}>
                            {doc.status === 'sent' && <Clock size={14} />}
                            {doc.status === 'viewed' && <Eye size={14} />}
                            {doc.status === 'signed' && <CheckCircle size={14} />}
                            {doc.status === 'artist_signed' && <PenTool size={14} />}
                            {doc.status === 'completed' && <UserCheck size={14} />}
                            {statusLabel(doc.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submissions */}
              <div className="detail-section">
                <h3><Upload size={16} /> Submissions ({artistDetail?.submissions?.length || 0})</h3>
                {(!artistDetail?.submissions || artistDetail.submissions.length === 0) ? (
                  <p className="no-data">No submissions yet</p>
                ) : (
                  <div className="documents-list">
                    {artistDetail.submissions.map(sub => (
                      <div key={sub.id} className="doc-item" data-testid={`submission-${sub.id}`}>
                        <div className="doc-info">
                          <span className="doc-title">{sub.title}</span>
                          <span className="doc-meta">
                            <span className={`doc-type ${sub.submission_type}`}>{sub.submission_type?.replace('_', ' ')}</span>
                            {formatDate(sub.created_at)}
                            {sub.files?.length > 0 && ` | ${sub.files.length} file${sub.files.length !== 1 ? 's' : ''}`}
                          </span>
                          {sub.description && <span className="doc-meta">{sub.description}</span>}
                          {sub.files?.length > 0 && (
                            <div className="submission-files">
                              {sub.files.map((file, i) => (
                                <a key={i} href={getFileUrl(file)} target="_blank" rel="noopener noreferrer" className="file-link">
                                  <ExternalLink size={12} /> File {i + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        {sub.files?.length > 0 && (
                          <button className="doc-action-btn" onClick={() => handleDownloadSubmission(sub.id)} title="Download files" data-testid={`download-sub-${sub.id}`}>
                            <Download size={13} /> Download
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Review Info */}
              {profile?.last_reviewed_by && (
                <div className="detail-section review-info">
                  <p>Last reviewed by <strong>{profile.last_reviewed_by}</strong> on {formatDate(profile.last_reviewed_at)}</p>
                  {artistDetail?.active !== undefined && (
                    <span className={`publish-badge ${artistDetail.active ? 'published' : 'draft'}`}>
                      {artistDetail.active ? 'Public' : 'Hidden'}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Document Generation Wizard Modal ── */}
      {showDocWizard && (
        <div className={`modal-overlay${wizardStep >= 3 ? ' wizard-fullscreen-overlay' : ''}`}>
          <div className={`modal-content doc-wizard-modal${wizardStep >= 3 ? ' wizard-fullscreen-modal' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FileText size={20} /> Generate Document for {selectedArtist?.artist_name}</h2>
              <button className="close-btn" onClick={closeDocWizard}><X size={20} /></button>
            </div>

            {/* Step indicator */}
            <div className="wizard-steps" data-testid="wizard-steps">
              {['Template', 'Fields', 'Edit', 'Preview'].map((label, i) => (
                <div key={i} className={`wizard-step ${wizardStep === i + 1 ? 'active' : wizardStep > i + 1 ? 'done' : ''}`}>
                  <span className="step-num">{i + 1}</span>
                  <span className="step-label">{label}</span>
                </div>
              ))}
            </div>

            {/* Step 1: Select Template */}
            {wizardStep === 1 && (
              <div className="wizard-body" data-testid="wizard-step-1">
                <p className="wizard-instruction">Choose a template to generate from:</p>
                {templates.length === 0 ? (
                  <p className="no-data" style={{ textAlign: 'center', padding: 40 }}>No templates yet. Create one in the Templates section first.</p>
                ) : (
                  <div className="template-select-list">
                    {templates.map(t => (
                      <button key={t.id} className="template-select-item" onClick={() => handleSelectTemplate(t)} data-testid={`select-template-${t.id}`}>
                        <div>
                          <strong>{t.title}</strong>
                          {t.description && <span className="tmpl-desc">{t.description}</span>}
                        </div>
                        <span className="tmpl-type">{t.document_type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Custom Fields */}
            {wizardStep === 2 && selectedTemplate && (
              <div className="wizard-body" data-testid="wizard-step-2">
                <p className="wizard-instruction">
                  Auto-fill fields (artist name, email, etc.) will be populated automatically.
                  {(selectedTemplate.custom_fields || []).length > 0
                    ? ' Fill in the custom fields below:'
                    : ' This template has no custom fields.'}
                </p>

                {(selectedTemplate.custom_fields || []).length > 0 && (
                  <div className="custom-fields-form">
                    {selectedTemplate.custom_fields.map(f => (
                      <div key={f.name} className="cf-input-group" data-testid={`cf-${f.name}`}>
                        <label>
                          {f.label} {f.required && <span className="cf-required">*</span>}
                        </label>
                        <input
                          type="text"
                          value={customFieldValues[f.name] || ''}
                          onChange={e => setCustomFieldValues(prev => ({ ...prev, [f.name]: e.target.value }))}
                          placeholder={`Enter ${f.label.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="wizard-option">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={requiresCountersign} onChange={e => setRequiresCountersign(e.target.checked)} />
                    Requires staff counter-signature after artist signs
                  </label>
                  {selectedTemplate?.content_html?.includes('{{staff_signature_block}}') && (
                    <p className="wizard-auto-detect">
                      <PenTool size={12} /> This template includes a staff signature block — counter-signature will be auto-required.
                    </p>
                  )}
                </div>

                <div className="wizard-nav">
                  <button className="wiz-btn secondary" onClick={() => setWizardStep(1)}>
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button className="wiz-btn primary" onClick={handlePopulateTemplate} disabled={generating} data-testid="populate-btn">
                    {generating ? <Loader2 size={14} className="spinner" /> : <ArrowRight size={14} />}
                    {generating ? 'Populating...' : 'Populate & Edit'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Edit Content */}
            {wizardStep === 3 && (
              <div className="wizard-body wizard-edit-body" data-testid="wizard-step-3">
                <p className="wizard-instruction">Review and edit the document content below. All tags have been replaced with actual data.</p>
                <InlineDocEditor html={populatedHtml} onChange={setPopulatedHtml} />
                <div className="wizard-nav">
                  <button className="wiz-btn secondary" onClick={() => setWizardStep(2)}>
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button className="wiz-btn primary" onClick={() => handlePreviewPdf(populatedHtml)} disabled={generating} data-testid="preview-btn">
                    {generating ? <Loader2 size={14} className="spinner" /> : <Eye size={14} />}
                    {generating ? 'Generating...' : 'Preview PDF'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Preview & Send */}
            {wizardStep === 4 && (
              <div className="wizard-body wizard-preview-body" data-testid="wizard-step-4">
                <p className="wizard-instruction">Review the final PDF below. If everything looks correct, send it to the artist's Legal Vault.</p>
                {previewUrl && (
                  <iframe src={previewUrl} className="preview-iframe" title="Document Preview" data-testid="preview-pdf" />
                )}
                <div className="wizard-nav">
                  <button className="wiz-btn secondary" onClick={() => { setWizardStep(3); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
                    <ArrowLeft size={14} /> Edit
                  </button>
                  <button className="wiz-btn primary send" onClick={handleSendDocument} disabled={generating} data-testid="send-document-btn">
                    {generating ? <Loader2 size={14} className="spinner" /> : <Send size={14} />}
                    {generating ? 'Sending...' : 'Send to Artist'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Counter-Sign Modal ── */}
      {countersignDoc && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><PenTool size={20} /> Counter-Sign Document</h2>
              <button className="close-btn" onClick={() => { setCountersignDoc(null); setStaffName(''); }}><X size={20} /></button>
            </div>
            <div className="doc-form" style={{ padding: 22 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--silver)', marginBottom: 12 }}>
                <strong>{countersignDoc.title}</strong> has been signed by the artist.
                Enter your name and draw your signature to counter-sign.
              </p>
              <div className="form-group">
                <label>Your Full Name (Label Representative)</label>
                <input
                  type="text"
                  value={staffName}
                  onChange={e => setStaffName(e.target.value)}
                  placeholder="e.g. George Barakat"
                  data-testid="staff-countersign-name"
                />
              </div>
              <div className="form-group">
                <label>Draw Your Signature</label>
                <div className="staff-sig-pad-wrapper" ref={staffPadWrapperRef} data-testid="staff-signature-pad">
                  <SignatureCanvas
                    ref={staffSigRef}
                    penColor="#000"
                    canvasProps={{
                      className: 'staff-sig-canvas',
                      width: staffPadWidth,
                      height: Math.max(180, Math.round(staffPadWidth * 0.35)),
                    }}
                  />
                  <button
                    className="staff-sig-clear"
                    onClick={() => staffSigRef.current?.clear()}
                    type="button"
                  >
                    <RotateCcw size={14} /> Clear
                  </button>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--silver)', marginTop: 8, opacity: 0.7 }}>
                Your IP address and timestamp will be recorded for verification purposes.
              </p>
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={() => { setCountersignDoc(null); setStaffName(''); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleCountersign} disabled={countersigning || !staffName.trim()} data-testid="confirm-countersign-btn">
                  {countersigning ? <Loader2 size={16} className="spinner" /> : <PenTool size={16} />}
                  {countersigning ? 'Signing...' : 'Counter-Sign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Inline TipTap Editor for Step 3 ──
const InlineDocEditor = ({ html, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: html,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  const ToolBtn = ({ action, isActive, icon: Icon, title }) => (
    <button
      type="button"
      className={`editor-tool-btn${isActive ? ' active' : ''}`}
      onClick={action}
      title={title}
      data-testid={`tool-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon size={15} />
    </button>
  );

  return (
    <div className="inline-doc-editor" data-testid="inline-editor">
      <div className="inline-editor-toolbar" data-testid="editor-toolbar">
        <ToolBtn action={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} icon={Bold} title="Bold" />
        <ToolBtn action={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} icon={Italic} title="Italic" />
        <ToolBtn action={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} icon={UnderlineIcon} title="Underline" />
        <span className="toolbar-divider" />
        <ToolBtn action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} icon={Heading1} title="H1" />
        <ToolBtn action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} icon={Heading2} title="H2" />
        <ToolBtn action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} icon={Heading3} title="H3" />
        <span className="toolbar-divider" />
        <ToolBtn action={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} icon={AlignLeft} title="Align Left" />
        <ToolBtn action={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} icon={AlignCenter} title="Align Center" />
        <ToolBtn action={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} icon={AlignRight} title="Align Right" />
        <span className="toolbar-divider" />
        <ToolBtn action={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} icon={List} title="Bullet List" />
        <ToolBtn action={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} icon={ListOrdered} title="Ordered List" />
        <span className="toolbar-divider" />
        <ToolBtn action={() => editor.chain().focus().undo().run()} isActive={false} icon={Undo} title="Undo" />
        <ToolBtn action={() => editor.chain().focus().redo().run()} isActive={false} icon={Redo} title="Redo" />
      </div>
      <div className="inline-editor-paper">
        <EditorContent editor={editor} className="inline-editor-content" />
      </div>
    </div>
  );
};

const renderSocialLinks = (social) => {
  if (!social || typeof social !== 'object') return <p className="no-data">No social links</p>;
  const entries = Object.entries(social).filter(([_, v]) => v);
  if (entries.length === 0) return <p className="no-data">No social links</p>;
  return (
    <div className="social-grid">
      {entries.map(([key, value]) => (
        <a key={key} href={value} target="_blank" rel="noopener noreferrer" className="social-chip">
          <Globe size={14} />
          {key.replace(/_/g, ' ').replace('url', '').replace('handle', '').trim()}
        </a>
      ))}
    </div>
  );
};

const renderStreamingIds = (streaming) => {
  if (!streaming || typeof streaming !== 'object') return null;
  const entries = Object.entries(streaming).filter(([_, v]) => v);
  if (entries.length === 0) return null;
  return (
    <div className="social-grid" style={{ marginTop: 8 }}>
      {entries.map(([key, value]) => (
        <span key={key} className="streaming-chip">
          <Music size={14} />
          {key.replace(/_id/, '').replace(/_/g, ' ')}: {value}
        </span>
      ))}
    </div>
  );
};

export default ArtistProfileReview;
